/**
 * SelectWithDynamicHintFilter — search-driven multi-select filter.
 *
 * Two-pane popover: left pane = available options (search-bound,
 * with a Pre-Filter / All segmented toggle), right pane = the user's
 * running selection (with an inline Clear action and an empty-state
 * card when nothing is picked yet). All chrome — shell, section
 * heads, segmented control, search input, option rows, empty state,
 * loader, footer — is now token-driven via
 * `SelectWithDynamicHintFilter.css` so the popover follows light /
 * dark theme automatically and reads as a member of the QHS design
 * system instead of a leftover MUI surface.
 *
 * Behaviour preserved 1:1 from the legacy implementation:
 *   - Buffered draft (`selectedOptions`) re-seeded from the committed
 *     filter value when the popover opens.
 *   - Apply commits the draft, Cancel / outside-click reverts to the
 *     value captured on open (`initTableFilter`).
 *   - `hasUnsavedChanges` blocks the outside-click revert while there
 *     are unsaved selections — the user must use Cancel or Apply
 *     explicitly when they've changed something.
 *   - The lightning-bolt button on each row still fires
 *     `applyCurrentFilter` (single-click "select + apply") so keyboard
 *     and mouse fast-paths stay intact.
 */
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import cx from 'classnames';
import isEqual from 'lodash/isEqual';
import Popover from '@shared/ui/Popover/Popover';
import { Button } from '@shared/ui';
import {
  EMPTY_FILTER_VALUE,
  fromSplitFilterValue,
  isEmptyFilterValue,
  toSplitFilterValue,
} from '@shared/lib/filterValue';
import SelectedSection from './widgets/SelectedSection';
import SearchSection from './widgets/SearchSection/SearchSection';
import { describeSplitValue } from '../../lib/describeFilter';
import './SelectWithDynamicHintFilter.css';

export const FILTER_MODES = { include: 'include', exclude: 'exclude' };

const ChevronSvg = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const SelectWithDynamicHintFilter = ({
  filterValue,
  dataKey,
  tableName,
  onChange,
  label, // eslint-disable-line no-unused-vars
  disabled = false,
  tableFilters,
  includeCurrentAccount,
  customAccountUuidForFilter,
  staticFilters,
  // Columns the API cannot filter negatively (a negated term on a
  // per-document bot-activity field is answered with 422). The mode
  // switch is not rendered and the draft can only ever be an include
  // list.
  disableExcludeMode = false,
}) => {
  const { tables, btn, scit, slit, formulas } = useSelector(state => state.i18n);
  const [open, setOpen] = useState(false);
  // The draft is always the split shape while the popover is open; it is
  // collapsed back to a plain array on commit when nothing is excluded,
  // so an include-only filter stores and sends exactly what it did
  // before exclusion existed.
  const [draft, setDraft] = useState(EMPTY_FILTER_VALUE);
  const [mode, setMode] = useState(FILTER_MODES.include);
  const [initTableFilter, setInitTableFilter] = useState([]);

  const updateOptions = () => {
    setDraft(filterValue ? toSplitFilterValue(filterValue) : EMPTY_FILTER_VALUE);
  };

  // Which list a click on an option row lands in, and which list it is
  // removed from. Both keyed off the current mode so a row click is
  // always a toggle within one list — never a move between the two.
  const toggleOption = (option, checked) => {
    setDraft(current => {
      const other = mode === FILTER_MODES.include ? 'exclude' : 'include';
      const list = current[mode];
      const nextList = checked
        ? [...new Set([...list, option])]
        : list.filter(item => item !== option);
      return { ...current, [mode]: nextList, [other]: current[other] };
    });
  };

  const removeOption = (option, list) => {
    setDraft(current => ({ ...current, [list]: current[list].filter(item => item !== option) }));
  };

  // Capture the committed filter value the moment the popover opens
  // so Cancel / outside-click can revert to it later.
  useEffect(() => {
    if (open) setInitTableFilter(filterValue);
    // Mode is a per-visit choice, not a remembered preference: the
    // popover always reopens on Include. Carrying Exclude over from a
    // previous visit is the mode's worst failure — the user opens the
    // filter, picks a value expecting to see it, and gets everything
    // else.
    else setMode(FILTER_MODES.include);
  }, [open]); // eslint-disable-line

  useEffect(() => {
    updateOptions();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!open) updateOptions();
  }, [filterValue]); // eslint-disable-line

  // `newSelectedValue` is the quick-apply path (the lightning bolt on a
  // row): commit the draft plus that one option. It lands in whichever
  // list the current mode points at, so the bolt means the same thing as
  // clicking the row and pressing Apply.
  const applyFilters = (event, newSelectedValue) => {
    const next = newSelectedValue
      ? { ...draft, [mode]: [...new Set([...draft[mode], newSelectedValue])] }
      : draft;
    setDraft(next);
    onChange(dataKey, fromSplitFilterValue(next));
    setOpen(false);
  };

  const resetSelectedOptionsAndClose = () => {
    setOpen(false);
    onChange(dataKey, initTableFilter);
    setDraft(initTableFilter ? toSplitFilterValue(initTableFilter) : EMPTY_FILTER_VALUE);
  };

  // True when the user has changed the draft since opening — used
  // to gate the outside-click revert. Without this, brushing past
  // the popover would discard changes silently.
  //
  // Compared in the split shape on both sides: the committed value may
  // be a plain array (include-only) while the draft is always split, and
  // comparing those two directly would report a change that is not one.
  const hasUnsavedChanges = useMemo(
    () => !isEqual(draft, toSplitFilterValue(filterValue || [])),
    [draft, filterValue]
  );

  const handlePopoverClose = () => {
    if (hasUnsavedChanges) return;
    resetSelectedOptionsAndClose();
  };

  // Display the committed value (not the draft) — the trigger
  // shouldn't visibly track a half-finished pick the user might
  // still cancel.
  // The trigger has to say which side a value is on, or an exclusion
  // reads as its own opposite — "Automated" on the button while the
  // table is showing everything but. `describeSplitValue` does that,
  // shared with the toolbar's filter pill.
  const triggerLabel = useMemo(
    () =>
      describeSplitValue(filterValue, { scit, slit, formulas, tables }) ||
      tables?.filter ||
      'Filter',
    [filterValue, scit, slit, formulas, tables]
  );

  const isFiltered = !isEmptyFilterValue(filterValue || []);

  const trigger = (
    <button
      type="button"
      onClick={() => !disabled && setOpen(o => !o)}
      disabled={disabled}
      className={cx('qhs-date-field', 'qhs-date-field--sm', 'qhs-date-field--full', {
        'qhs-date-field--placeholder': !isFiltered,
        'qhs-date-field--highlighted': isFiltered,
      })}
    >
      <span className="qhs-date-field__value">{triggerLabel}</span>
      <span className="qhs-date-field__icon">{ChevronSvg}</span>
    </button>
  );

  return (
    <Popover open={open} onClose={handlePopoverClose} trigger={trigger} placement="bottom-start">
      <div className="qhs-dynh-shell">
        <div className="qhs-dynh-body">
          <SearchSection
            draft={draft}
            mode={mode}
            onModeChange={setMode}
            onToggleOption={toggleOption}
            handleClose={applyFilters}
            dataKey={dataKey}
            open={open}
            tableName={tableName}
            tableFilters={tableFilters}
            includeCurrentAccount={includeCurrentAccount}
            customAccountUuidForFilter={customAccountUuidForFilter}
            staticFilters={staticFilters}
            disableExcludeMode={disableExcludeMode}
          />
          <SelectedSection
            draft={draft}
            onRemove={removeOption}
            handleClose={applyFilters}
            clearAllFilter={() => setDraft(EMPTY_FILTER_VALUE)}
          />
        </div>
        <div className="qhs-dynh-foot">
          <Button variant="ghost" size="sm" onClick={resetSelectedOptionsAndClose}>
            {btn?.cancel || 'Cancel'}
          </Button>
          <Button variant="primary" size="sm" onClick={applyFilters}>
            {btn?.apply || 'Apply'}
          </Button>
        </div>
      </div>
    </Popover>
  );
};

export default SelectWithDynamicHintFilter;
